import React, { useState } from 'react';
import { Search, BookOpen, Users, Camera, Settings, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Help() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Topics', icon: BookOpen },
    { id: 'getting-started', name: 'Getting Started', icon: CheckCircle },
    { id: 'organizers', name: 'For Event Organizers', icon: Users },
    { id: 'attendees', name: 'For Attendees', icon: Camera },
    { id: 'troubleshooting', name: 'Troubleshooting', icon: AlertCircle },
    { id: 'advanced', name: 'Advanced Features', icon: Settings },
  ];

  const guides = [
    {
      category: 'getting-started',
      title: 'Quick Start Guide',
      description: 'Get up and running with EventPic Live in 5 minutes',
      content: [
        {
          step: '1. Create Your First Event',
          details: 'Click "New Event" from your dashboard. Enter the event name, date, and location. The system will automatically generate a unique QR code for attendee registration.',
        },
        {
          step: '2. Display the QR Code',
          details: 'Download or print the QR code and display it at your event entrance. Attendees will scan this code to register.',
        },
        {
          step: '3. Attendees Register',
          details: 'Attendees download the EventPic Live mobile app, scan your QR code, and take a quick selfie. Registration takes about 30 seconds.',
        },
        {
          step: '4. Upload Photos',
          details: 'As your photographer takes photos, upload them through the dashboard or mobile app. Photos are processed in real-time.',
        },
        {
          step: '5. Automatic Delivery',
          details: 'Our AI identifies faces in each photo and automatically sends photos to attendees via text message within 2-5 minutes.',
        },
      ],
    },
    {
      category: 'organizers',
      title: 'Setting Up Your Event',
      description: 'Best practices for event organizers',
      content: [
        {
          step: 'Choose the Right Plan',
          details: 'Select a plan based on expected attendance. Starter (up to 100 attendees), Professional (up to 500), or Enterprise (unlimited). You can upgrade anytime.',
        },
        {
          step: 'Customize Your Event',
          details: 'Add your event logo, custom branding, and welcome message. Enterprise customers can use custom domains for their photo galleries.',
        },
        {
          step: 'Set Up Multiple Photographers',
          details: 'Invite team members or photographers by email. Each gets their own login to upload photos. Track who uploaded which photos in the dashboard.',
        },
        {
          step: 'Test Before the Event',
          details: 'Do a test run 1-2 days before. Register a few test attendees, upload sample photos, and verify the delivery flow works correctly.',
        },
        {
          step: 'Monitor in Real-Time',
          details: 'During the event, use the dashboard to monitor: total photos uploaded, attendees registered, photos delivered, and system status.',
        },
      ],
    },
    {
      category: 'organizers',
      title: 'Managing Photo Uploads',
      description: 'How to upload and organize event photos',
      content: [
        {
          step: 'Upload Methods',
          details: 'Upload via: (1) Dashboard web interface - drag & drop or select files, (2) Mobile app - take photos directly or choose from gallery, (3) API integration for automated workflows (Enterprise only).',
        },
        {
          step: 'Supported Formats',
          details: 'We support JPG, PNG, and HEIC formats. Photos are automatically optimized for mobile delivery. Maximum file size: 25MB per photo.',
        },
        {
          step: 'Batch Uploads',
          details: 'You can upload up to 100 photos at once. For larger batches, split into multiple uploads or contact support for bulk import assistance.',
        },
        {
          step: 'Photo Processing Time',
          details: 'Photos are typically processed within 2-5 minutes. Processing includes: face detection, face recognition, matching to attendees, and delivery via text.',
        },
        {
          step: 'Manual Review',
          details: 'Review all photos in the dashboard before delivery. You can delete unwanted photos or manually assign photos to specific attendees if needed.',
        },
      ],
    },
    {
      category: 'attendees',
      title: 'How Attendees Get Their Photos',
      description: 'The attendee experience from registration to photo delivery',
      content: [
        {
          step: '1. Download the App',
          details: 'Attendees download "EventPic Live" from the App Store (iOS) or Google Play (Android). The app is free and requires no account creation.',
        },
        {
          step: '2. Scan the QR Code',
          details: 'At the event entrance, attendees open the app and scan the event QR code displayed by the organizer. This links them to the specific event.',
        },
        {
          step: '3. Take a Selfie',
          details: 'The app prompts for a quick selfie. This creates a face profile for AI matching. The selfie should be: well-lit, face clearly visible, no sunglasses or masks.',
        },
        {
          step: '4. Enter Phone Number',
          details: 'Attendees enter their mobile number to receive photos via text. They\'ll get a verification code to confirm the number.',
        },
        {
          step: '5. Receive Photos Automatically',
          details: 'As photos are taken and uploaded, our AI identifies the attendee\'s face and sends photos directly to their phone via text message. No action needed!',
        },
        {
          step: '6. Browse Full Gallery',
          details: 'Attendees can also browse all event photos in the app and manually select additional photos they\'re in. They receive a personalized gallery link.',
        },
      ],
    },
    {
      category: 'troubleshooting',
      title: 'Face Recognition Issues',
      description: 'What to do when faces aren\'t being recognized',
      content: [
        {
          step: 'Poor Selfie Quality',
          details: 'Common issues: too dark, face partially covered, sunglasses, extreme angles. Solution: Have attendees retake their selfie in good lighting with face clearly visible.',
        },
        {
          step: 'Multiple People in Photo',
          details: 'Our AI can detect up to 20 faces per photo. If a photo has more people, it may take longer to process. Very crowded photos (50+ people) may have reduced accuracy.',
        },
        {
          step: 'Changed Appearance',
          details: 'If attendees significantly change appearance during the event (put on/remove glasses, hats, masks), recognition accuracy may decrease. They can update their selfie in the app.',
        },
        {
          step: 'Low-Quality Event Photos',
          details: 'Blurry, dark, or low-resolution photos reduce recognition accuracy. Ensure photographers use good lighting and focus. Minimum recommended resolution: 1920x1080.',
        },
        {
          step: 'Manual Assignment',
          details: 'If a photo isn\'t automatically matched, attendees can browse all event photos and manually select themselves. Organizers can also manually assign photos in the dashboard.',
        },
      ],
    },
    {
      category: 'troubleshooting',
      title: 'Photo Delivery Problems',
      description: 'Troubleshooting text message delivery',
      content: [
        {
          step: 'Photos Not Received',
          details: 'Check: (1) Phone number entered correctly during registration, (2) Phone can receive text messages, (3) Not blocked as spam. Attendees can check their gallery link in the app.',
        },
        {
          step: 'Delayed Delivery',
          details: 'Normal processing time is 2-5 minutes. During peak times (100+ photos uploaded simultaneously), it may take up to 10 minutes. Check dashboard for processing status.',
        },
        {
          step: 'International Numbers',
          details: 'We support international SMS delivery to 200+ countries. Some carriers may have delays. Attendees can always access photos via the web gallery link.',
        },
        {
          step: 'Carrier Blocking',
          details: 'Some carriers may block automated messages. If attendees aren\'t receiving texts, have them check spam/blocked messages or add our number to contacts.',
        },
        {
          step: 'Resend Photos',
          details: 'Organizers can manually resend photos to specific attendees from the dashboard. Go to Event → Attendees → Select User → Resend Photos.',
        },
      ],
    },
    {
      category: 'advanced',
      title: 'API Integration',
      description: 'Integrate EventPic Live with your existing systems (Enterprise only)',
      content: [
        {
          step: 'API Access',
          details: 'Enterprise plans include full API access. Generate API keys from Settings → API Keys. Keep your keys secure and never share them publicly.',
        },
        {
          step: 'Automated Photo Upload',
          details: 'Use the Upload API to automatically send photos from your camera system or photo booth. Supports batch uploads and webhook notifications.',
        },
        {
          step: 'Event Management',
          details: 'Create and manage events programmatically. Useful for event management platforms that want to integrate EventPic Live into their workflow.',
        },
        {
          step: 'Attendee Data Export',
          details: 'Export attendee lists, photo delivery stats, and engagement metrics via API. Available in JSON and CSV formats.',
        },
        {
          step: 'Webhook Notifications',
          details: 'Set up webhooks to receive real-time notifications when: new attendee registers, photo is uploaded, photo is delivered, event ends.',
        },
      ],
    },
    {
      category: 'advanced',
      title: 'Custom Branding',
      description: 'White-label options for Enterprise customers',
      content: [
        {
          step: 'Custom Domain',
          details: 'Use your own domain for photo galleries (e.g., photos.yourevent.com). Configure DNS settings and SSL certificates through the dashboard.',
        },
        {
          step: 'Branded Mobile App',
          details: 'Enterprise customers can request a white-labeled version of the mobile app with your logo, colors, and branding. Contact support for pricing.',
        },
        {
          step: 'Custom Email Templates',
          details: 'Customize the email templates sent to attendees with your branding, logo, and messaging. Edit templates in Settings → Branding.',
        },
        {
          step: 'Remove EventPic Branding',
          details: 'Enterprise plans can remove all EventPic Live branding from galleries, emails, and text messages. Enable in Settings → Branding → White Label Mode.',
        },
      ],
    },
    {
      category: 'advanced',
      title: 'Analytics & Reporting',
      description: 'Track event performance and engagement',
      content: [
        {
          step: 'Real-Time Dashboard',
          details: 'Monitor live stats during your event: attendees registered, photos uploaded, photos delivered, average delivery time, engagement rate.',
        },
        {
          step: 'Post-Event Reports',
          details: 'After the event, generate detailed reports: total reach, photos per attendee, delivery success rate, peak upload times, attendee demographics.',
        },
        {
          step: 'Export Data',
          details: 'Export all event data to CSV or Excel: attendee list with contact info, photo delivery logs, engagement metrics, revenue attribution.',
        },
        {
          step: 'Engagement Metrics',
          details: 'Track: gallery views, photo downloads, social shares, text message open rates, app usage patterns.',
        },
      ],
    },
  ];

  const filteredGuides = guides.filter(guide => {
    const matchesCategory = activeCategory === 'all' || guide.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      guide.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      guide.content.some(item => 
        item.step.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.details.toLowerCase().includes(searchQuery.toLowerCase())
      );
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/events')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Help & Documentation
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Comprehensive guides for event organizers and attendees
                </p>
              </div>
            </div>
            <BookOpen className="w-12 h-12 text-blue-600 dark:text-blue-400" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        {/* Category Filters */}
        <div className="mb-8 flex flex-wrap gap-3">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{category.name}</span>
              </button>
            );
          })}
        </div>

        {/* Guides */}
        <div className="space-y-6">
          {filteredGuides.length > 0 ? (
            filteredGuides.map((guide, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {guide.title}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {guide.description}
                  </p>
                </div>
                <div className="p-6">
                  <div className="space-y-6">
                    {guide.content.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            {item.step}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                            {item.details}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                No guides found matching "{searchQuery}"
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
              >
                Clear search
              </button>
            </div>
          )}
        </div>

        {/* Still Need Help */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-gray-800 dark:to-gray-700 rounded-lg p-8 text-center border border-blue-200 dark:border-gray-600">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Still Need Help?
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
            Can't find what you're looking for? Our support team is here to help you get the most out of EventPic Live.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@eventpiclive.com"
              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Email Support
            </a>
            <a
              href="https://eventpiclive.com/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>

      
    </div>
  );
}
